import { usePost } from "@albertine/api-client";
import type { MemberDataFirestoreRecord } from "@albertine/types";

import { isBefore, isSameDay } from "date-fns";
import React, { useEffect, useReducer, useRef, useState } from "react";
import { useHistory } from "react-router";
import {
    type ChatPageState,
    createPaginatedChatMessageState,
} from "../../ChatPaginatedMessageState";
import { useAgents } from "../../common/hooks/useAgents";
import type { Agent } from "../../common/types/Agent";
import type { Conversation } from "../../common/types/Conversation";
import type {
    InternalNoteWithMention,
    Message,
    MessageOrInternalNote,
    MessageOrInternalNoteOrAiSummary,
} from "../../common/types/Message";
import type { Request } from "../../common/types/Request";
import { EventEmitter, type EventListener } from "../../EventEmitter";
import { queryOpenTasks as queryInternalNotesWithOpenMention } from "../../FirebaseBackend";
import { listenToQuery } from "../../FirebaseListeners";
import { memberNameBySurname } from "../../utils/name.util";
import { compareBySentAt } from "../../utils/sort.util";
import { formatChatHeaderDate, formatLocalDate } from "../../utils/time.util";
import { fromTimestampToDate, toTimestamp } from "../../utils/timestamp.util";
import ButtonPill from "../Common/ButtonPill";
import HandleDragAndDrop from "../HandleDragAndDrop";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import "./Chat.css";
import { ChatSelectionBar } from "@prototypes/chat-message/ChatSelectionBar";
import classNames from "classnames";
import routes, {
    findChatMessageIdFromPath,
    findMemberIdFromPath,
} from "../../utils/routes";
import {
    revokeCssClass,
    scrollRefToBottom,
    scrollToElement,
} from "../../utils/window.util";
import { Flex } from "../Common/Flex";
import Icon from "../Common/Icon";
import Label from "../Common/Label";
import Overlay from "../Common/Overlay";
import ChangeMessageRequest from "./ChangeMessageRequest";
import { createContextKey } from "./ChatContext";
import { highlightedCssClassName } from "./ChatInternalNote";
import ChatMessageOrNote from "./ChatMessageOrNote";
import ChatReply from "./ChatReply";
import { ChatSelectionProvider, useChatSelection } from "./ChatSelectionContext";
import { canMarkMessagesAsRead, unreadMessagesPerThread } from "./chat.util";
import RequestStatusChatBadge from "./RequestStatusChatBadge";
import { useCreateRequestFromMessages } from "./useCreateRequestFromMessages";

const pixelsUntilScrollToBottomShowsUp = 500;

const chatMessageSelector = (messageId: string) => `chat_message__${messageId}`;
const chatMessagesClassName = "chat-messages";

function scrollToMessage(messageId: string) {
    const elementId = chatMessageSelector(messageId);
    revokeCssClass(elementId, highlightedCssClassName);
    scrollToElement(elementId, false, true);
}

const useScrollChatIntoMessage = (
    chatState: ChatPageState | undefined,
    conversationId: string | undefined,
) => {
    useEffect(() => {
        const scrollListener: EventListener = {
            name: "scrollChatIntoMessage",
            listener: (params) => {
                if (chatState) {
                    void chatState.findAndSubscribe(params.messageId).then(() => {
                        scrollToMessage(params.messageId);
                    });
                }
            },
        };

        EventEmitter.addListener(scrollListener);

        return () => {
            EventEmitter.removeListener(scrollListener);
        };
    }, [chatState, conversationId]);
};

function useConversationTitle(
    memberName: string | undefined,
    unreadMessageCount: number | null | undefined,
) {
    const baseTitle = "LMT";

    useEffect(() => {
        const titleElement = document.querySelector("head title");

        if (titleElement) {
            titleElement.innerHTML = `${baseTitle} - ${
                unreadMessageCount && unreadMessageCount > 0
                    ? `(${unreadMessageCount}) `
                    : ""
            }${memberName}`;
        }

        return () => {
            if (titleElement) {
                titleElement.innerHTML = baseTitle;
            }
        };
    }, [memberName, unreadMessageCount]);
}

function DateHeader(props: { currentMessage: MessageOrInternalNote }) {
    const { currentMessage } = props;
    return (
        <div
            key={
                currentMessage.sentAt
                    ? toTimestamp(currentMessage.sentAt).toMillis()
                    : undefined
            }
            className="chat-message__date-header"
        >
            <div>
                {currentMessage.sentAt
                    ? formatChatHeaderDate(
                          currentMessage.sentAt
                              ? toTimestamp(currentMessage.sentAt)
                              : null,
                      )
                    : ""}
            </div>
        </div>
    );
}

function isMemberMessageUnread(
    conversation: Conversation,
    messageOrNote: MessageOrInternalNote,
) {
    if (messageOrNote.isInternalNote) return false;
    if (messageOrNote.sentByAgent) return false;
    if (!conversation.lastSeenMessageDateByAgent) return true;

    return conversation.unreadMessagesByAgent
        ? conversation.unreadMessagesByAgent
              .map((it) => it.message)
              .includes(messageOrNote.id)
        : false;
}

function MessageComponent(props: {
    agents: Agent[];
    conversation: Conversation;
    message: MessageOrInternalNoteOrAiSummary;
    member: MemberDataFirestoreRecord;
    currentAgent: Agent;
    isUnread: boolean;
    notPartOfRequest: boolean;
    currentRequest: Request | undefined;
    isLatestAiSummary?: boolean;
}) {
    const {
        agents,
        conversation,
        currentAgent,
        message,
        member,
        isUnread,
        notPartOfRequest,
        currentRequest,
        isLatestAiSummary,
    } = props;

    return (
        <ChatMessageOrNote
            idSelector={chatMessageSelector(message.id)}
            message={message}
            conversation={conversation}
            currentAgent={currentAgent}
            currentMember={member}
            agents={agents}
            isUnread={isUnread}
            notPartOfRequest={notPartOfRequest}
            currentRequest={currentRequest}
            isLatestAiSummary={isLatestAiSummary}
        />
    );
}

function PagedChatMessagesLoadMore(props: {
    children: React.ReactNode;
    disabled: boolean;
    onClick: () => void;
}) {
    const { children, disabled, onClick } = props;
    return (
        <button
            type="button"
            className="chat-messages__paged__load-more"
            disabled={disabled}
            onClick={onClick}
        >
            {children}
        </button>
    );
}

function ChatUnreadHeader(props: {
    conversation: Conversation;
    member: MemberDataFirestoreRecord;
    request: Request | undefined;
}) {
    const { conversation, member, request } = props;
    const [loadingMarkMessageAsRead, setLoadingMarkMessageAsRead] =
        useState<boolean>(false);
    const { mutateAsync: markConversationMessagesRead } = usePost(
        "agentMarkConversationMessagesRead",
    );
    const history = useHistory();

    const { unreadInRequestThread, unreadTotal } = unreadMessagesPerThread(
        request,
        conversation,
    );
    const showButtonPill = unreadTotal !== 0;

    const onClickMarkMessageAsRead = async () => {
        setLoadingMarkMessageAsRead(true);
        await markConversationMessagesRead({
            param: {
                memberId: member.id,
                conversationId: conversation.id,
            },
            json: { requestId: request?.id ?? null },
        });
        setLoadingMarkMessageAsRead(false);
    };

    const navigateToFirstUnreadMessage = () => {
        const unreadMessage = conversation.unreadMessagesByAgent
            ? conversation.unreadMessagesByAgent[0]
            : null;

        if (unreadMessage) {
            history.push(
                routes.chatMessage(
                    member.id,
                    unreadMessage.message,
                    unreadMessage.request || undefined,
                ),
            );
        }
    };

    const buttonLabel = (() => {
        if (request) {
            const unreadCount =
                unreadInRequestThread !== 0 ? unreadInRequestThread : unreadTotal;

            return `${unreadCount} unread message${unreadCount > 1 ? "s" : ""}`;
        }

        return `${unreadTotal} unread message${unreadTotal > 1 ? "s" : ""}`;
    })();

    return showButtonPill ? (
        <div className="chat-messages__unread-header">
            {canMarkMessagesAsRead(request, conversation.unreadMessagesByAgent) ? (
                <ButtonPill
                    dataTooltip="Mark as read"
                    text={buttonLabel}
                    onClick={onClickMarkMessageAsRead}
                    icon="Check"
                    loading={loadingMarkMessageAsRead}
                />
            ) : (
                <ButtonPill
                    dataTooltip="Go to the message"
                    icon="OpenInNew"
                    text={buttonLabel}
                    loading={loadingMarkMessageAsRead}
                    onClick={() => {
                        navigateToFirstUnreadMessage();
                    }}
                />
            )}
        </div>
    ) : null;
}

function ChatPage(props: {
    agents: Agent[];
    currentAgent: Agent;
    conversation: Conversation;
    member: MemberDataFirestoreRecord;
    page: ChatPageState;
    root: ChatPageState;
    request: Request | undefined;
    shouldShowUnreadMessagesPill: (
        message: MessageOrInternalNote,
        isMessageUnread: boolean,
    ) => boolean;
    isTopicFilterOn: boolean;
}) {
    const {
        agents,
        currentAgent,
        conversation,
        member,
        page,
        root,
        request,
        shouldShowUnreadMessagesPill,
        isTopicFilterOn,
    } = props;

    const shouldUseFilterModeByDefault = request !== undefined;

    let dateHeadersShown: Date[] = [];
    const shouldShowDateHeader = (
        messages: MessageOrInternalNote[],
        currentMessageIndex: number,
        isCollapsed: boolean,
    ): boolean => {
        const currentMessage = messages[currentMessageIndex];
        const previousMessage = messages[currentMessageIndex - 1];
        const previousDate = previousMessage?.sentAt
            ? fromTimestampToDate(previousMessage.sentAt)
            : undefined;
        const thisDate = currentMessage?.sentAt
            ? fromTimestampToDate(currentMessage.sentAt)
            : undefined;
        const sameDay =
            previousDate && thisDate ? isSameDay(previousDate, thisDate) : false;
        const isBeforePrevious =
            previousDate && thisDate ? isBefore(previousDate, thisDate) : false;
        const showHeader = !previousMessage || (isBeforePrevious && !sameDay);

        const sentAtDate = fromTimestampToDate(currentMessage.sentAt);
        const dateHeaderAlreadyShown = dateHeadersShown.some((date) =>
            isSameDay(date, sentAtDate),
        );

        const isFirstPage = page === root;

        if (showHeader && !isFirstPage && !dateHeaderAlreadyShown) {
            if (!isCollapsed) {
                dateHeadersShown = [sentAtDate, ...dateHeadersShown];
            }
            return true;
        }

        return false;
    };

    return (
        <div id={page.id()}>
            {page.showLoadMore() && (
                <PagedChatMessagesLoadMore
                    disabled={page.isLoading()}
                    onClick={() => page.subscribeToChanges()}
                >
                    {page.isLoading() ? (
                        <LoadingSpinner />
                    ) : (
                        <div>
                            Load messages
                            <br />
                            <span className="chat-messages__paged__load-more--small">
                                {formatLocalDate(page.from)}
                                {page.to && ` - ${formatLocalDate(page.to)}`}
                            </span>
                        </div>
                    )}
                </PagedChatMessagesLoadMore>
            )}

            {page.id() !== root.id() && page.isLoading() && (
                <div className="chat-messages__paged__center">
                    <LoadingSpinner.Big />
                </div>
            )}

            {page.hasLoaded() &&
                page
                    .sortOrGroupTimelineItems(request?.id, request?.createdFromMessage)
                    .map((group) => {
                        const notPartOfCurrentRequest = group.request !== request?.id;
                        const highLightGroup =
                            shouldUseFilterModeByDefault && notPartOfCurrentRequest;

                        if (
                            shouldUseFilterModeByDefault &&
                            isTopicFilterOn &&
                            (group.request === undefined || notPartOfCurrentRequest)
                        ) {
                            return [];
                        }

                        // Find the latest AI summary in this group
                        const aiSummaries = group.timelineItems.filter(
                            (item) => "summaryType" in item,
                        );
                        const latestAiSummaryId =
                            aiSummaries.length > 0
                                ? aiSummaries.sort(compareBySentAt)[0].id
                                : null;

                        const messages = group.timelineItems.map(
                            (messageOrNote, messageIndex, timelineItems) => {
                                const isMessageUnread = isMemberMessageUnread(
                                    conversation,
                                    messageOrNote as MessageOrInternalNote,
                                );
                                const isLatestAiSummary =
                                    messageOrNote.id === latestAiSummaryId;
                                return (
                                    <React.Fragment key={messageOrNote.id}>
                                        {shouldShowDateHeader(
                                            timelineItems as MessageOrInternalNote[],
                                            messageIndex,
                                            !highLightGroup,
                                        ) && (
                                            <DateHeader
                                                currentMessage={
                                                    messageOrNote as MessageOrInternalNote
                                                }
                                            />
                                        )}
                                        {shouldShowUnreadMessagesPill(
                                            messageOrNote as MessageOrInternalNote,
                                            isMessageUnread,
                                        ) && (
                                            <ChatUnreadHeader
                                                conversation={conversation}
                                                member={member}
                                                request={request}
                                            />
                                        )}
                                        <MessageComponent
                                            message={messageOrNote}
                                            conversation={conversation}
                                            currentAgent={currentAgent}
                                            member={member}
                                            agents={agents}
                                            isUnread={isMessageUnread}
                                            notPartOfRequest={highLightGroup}
                                            currentRequest={request}
                                            isLatestAiSummary={isLatestAiSummary}
                                        />
                                    </React.Fragment>
                                );
                            },
                        );

                        return messages;
                    })}
        </div>
    );
}

function PagedChatMessages(props: {
    tick: number; // Used to trigger ChatPageState re-render
    agents: Agent[];
    conversation: Conversation;
    currentAgent: Agent;
    member: MemberDataFirestoreRecord;
    chatPagedState: ChatPageState;
    request: Request | undefined;
}) {
    const { agents, conversation, currentAgent, member, chatPagedState, request } =
        props;

    // capture the first unread member message and show unread message
    // header pill before first unread member message
    let firstUnreadMemberMessage: string | null = null;
    const shouldShowUnreadMessagesPill = (
        messageOrNote: MessageOrInternalNote,
        isMessageUnread: boolean,
    ) => {
        if (!firstUnreadMemberMessage && isMessageUnread) {
            firstUnreadMemberMessage = messageOrNote.id;
        }
        return firstUnreadMemberMessage === messageOrNote.id;
    };

    const keepScrollPosition = (page: ChatPageState | undefined) => {
        if (!page) return;

        const pageInDom = document.querySelector(`#${page.id()}`);
        const chatMessages = document.querySelector(`.${chatMessagesClassName}`);
        if (chatMessages && pageInDom) {
            chatMessages.scrollTop += pageInDom.clientHeight;
        }
    };

    const [isTopicFilterOn, setIsTopicFilterOn] = useState<boolean>(
        request !== undefined,
    );

    useEffect(() => {
        const toggleThreadedChat: EventListener = {
            name: "toggleThreadedChat",
            listener: (params) => {
                setIsTopicFilterOn(params.enabled);
            },
        };

        EventEmitter.addListener(toggleThreadedChat);

        return () => {
            EventEmitter.removeListener(toggleThreadedChat);
        };
    }, []);

    return (
        <>
            {chatPagedState.last().canCreatePreviousPage() &&
                !chatPagedState.last().isLoading() && (
                    <PagedChatMessagesLoadMore
                        disabled={chatPagedState.last().isLoading()}
                        onClick={async () => {
                            const previousPage = chatPagedState
                                .last()
                                .createPreviousPage();
                            await previousPage?.subscribeToChanges();
                            keepScrollPosition(previousPage);
                        }}
                    >
                        <div>
                            See older messages
                            <br />
                            <span className="chat-messages__paged__load-more--small">
                                {formatLocalDate(
                                    chatPagedState.last().calculateFromDate(),
                                )}
                            </span>
                        </div>
                    </PagedChatMessagesLoadMore>
                )}
            {chatPagedState
                .listAll()
                .reverse()
                .map((page) => (
                    <ChatPage
                        key={`${page.id()}-${page.version}`}
                        agents={agents}
                        currentAgent={currentAgent}
                        conversation={conversation}
                        member={member}
                        page={page}
                        root={chatPagedState}
                        request={request}
                        shouldShowUnreadMessagesPill={shouldShowUnreadMessagesPill}
                        isTopicFilterOn={isTopicFilterOn}
                    />
                ))}
        </>
    );
}

function ChatSelectionActionBar(props: {
    member: MemberDataFirestoreRecord;
    currentAgent: Agent;
    loadedMessages: Message[];
}) {
    const { member, currentAgent, loadedMessages } = props;
    const { selecting, selectedIds, clearSelection } = useChatSelection();
    const [editTopicOpen, setEditTopicOpen] = useState(false);
    const { createRequestFromMessages, isPending } = useCreateRequestFromMessages({
        memberId: member.id,
        assignedToAgentId: currentAgent.id,
        onCreated: clearSelection,
    });

    if (!selecting) {
        return null;
    }

    const selectedIdSet = new Set(selectedIds);
    const selectedMessages = loadedMessages
        .filter((message) => selectedIdSet.has(message.id))
        .sort((a, b) => compareBySentAt(a, b));

    return (
        <>
            <ChatSelectionBar
                count={selectedIds.length}
                creating={isPending}
                onCancel={clearSelection}
                onEditTopic={() => setEditTopicOpen(true)}
                onNewRequest={() => createRequestFromMessages(selectedIds)}
            />
            {editTopicOpen && (
                <Overlay
                    className="light"
                    onClose={() => {
                        setEditTopicOpen(false);
                        return false;
                    }}
                >
                    <ChangeMessageRequest
                        member={member}
                        conversationId={member.conversation}
                        messages={selectedMessages}
                        close={() => setEditTopicOpen(false)}
                        onApplied={() => {
                            setEditTopicOpen(false);
                            clearSelection();
                        }}
                    />
                </Overlay>
            )}
        </>
    );
}

type Props = {
    conversation: Conversation;
    request?: Request | undefined;
    currentAgent: Agent;
    member: MemberDataFirestoreRecord;
};

function Chat(props: Props) {
    const { conversation, currentAgent, request, member } = props;

    const history = useHistory();
    const { mutate: clearAgentConversationUnseenReactions } = usePost(
        "agentClearConversationUnseenReactions",
    );

    const [allInternalNotesWithOpenMention, setAllInternalNotesWithOpenMention] =
        useState<InternalNoteWithMention[] | null>(null);
    const internalNotesWithOpenMention = request?.id
        ? (allInternalNotesWithOpenMention ?? []).filter(
              (note) => note.request === request.id,
          )
        : allInternalNotesWithOpenMention;

    const [showScrollToBottom, setShowScrollToBottom] = useState<boolean>(false);
    const [showNewMessagesIndicator, setShowNewMessagesIndicator] =
        useState<boolean>(false);
    const chatMessagesRef = useRef<HTMLDivElement>(null);
    useConversationTitle(
        memberNameBySurname(member),
        conversation?.unreadMessagesCountByAgent,
    );
    const agents = useAgents();

    const [chatUpdateTick, forceUpdateChat] = useReducer((x) => x + 1, 0);
    const [chatPaginatedState, setChatPaginatedState] = useState<ChatPageState>();

    const chatPaginatedStateRef = useRef<ChatPageState | undefined>(undefined);
    const [loadedMessages, setLoadedMessages] = useState<Message[]>([]);

    useScrollChatIntoMessage(chatPaginatedState, conversation?.id);

    const changeContextKey = (id: string) => {
        EventEmitter.emit({
            name: "changeChatContextKey",
            params: { id },
        });
    };

    const isScrolledNearBottom = (
        threshold: number = pixelsUntilScrollToBottomShowsUp,
    ): boolean => {
        if (chatMessagesRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = chatMessagesRef.current;
            const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
            return distanceFromBottom <= threshold;
        }
        return true;
    };

    useEffect(() => {
        changeContextKey(createContextKey(member, request));
    }, [member, request]);

    useEffect(() => {
        if (!conversation) return () => {};

        const chatState = createPaginatedChatMessageState({
            conversation,
            requestId: request?.id,
            createdFromMessageId: request?.createdFromMessage,
            targetedMessageId: findChatMessageIdFromPath(),
            onChanges: () => {
                forceUpdateChat();
                setLoadedMessages(
                    chatPaginatedStateRef.current?.getAllMessages() ?? [],
                );
            },
            onNewMessages: () => {
                if (isScrolledNearBottom()) {
                    // HACK: setTimeout to push scroll to next event cycle after React 18 state updates
                    // This is needed because chatPaginatedState is set after chat initialization,
                    // causing scroll operations to execute before message list content is in DOM
                    setTimeout(() => {
                        scrollRefToBottom(chatMessagesRef);
                    }, 0);
                    setShowNewMessagesIndicator(false);
                } else {
                    setShowNewMessagesIndicator(true);
                }
            },
            afterInitialLoad: (targetedMessage) => {
                // HACK: setTimeout to push scroll to next event cycle after React 18 state updates
                // This is needed because chatPaginatedState is set after chat initialization,
                // causing scroll operations to execute before message list content is in DOM
                setTimeout(() => {
                    if (targetedMessage) {
                        scrollToMessage(targetedMessage);
                    } else {
                        scrollRefToBottom(chatMessagesRef);
                    }
                }, 0);
            },
        });

        chatPaginatedStateRef.current = chatState;
        setChatPaginatedState(chatState);
        setLoadedMessages(chatState.getAllMessages());

        const unsubscribe = () => {
            chatState.unsubscribe();
            chatPaginatedStateRef.current = undefined;
            setChatPaginatedState(undefined);
            setLoadedMessages([]);
        };

        return unsubscribe;
    }, [conversation?.id, request?.id]);

    useEffect(() => {
        if (!conversation) return () => {};

        clearAgentConversationUnseenReactions({
            param: {
                memberId: member.id,
                conversationId: conversation.id,
            },
        });

        return listenToQuery<InternalNoteWithMention[]>(
            queryInternalNotesWithOpenMention(conversation.id),
            (mention) => {
                setAllInternalNotesWithOpenMention(
                    mention.map((it) => ({
                        ...it,
                        isInternalNote: true,
                    })),
                );
            },
        );
    }, [clearAgentConversationUnseenReactions, conversation?.id, member.id]);

    const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
        if (!showScrollToBottom) {
            if (
                event.currentTarget.scrollHeight -
                    (event.currentTarget.scrollTop + event.currentTarget.offsetHeight) >
                pixelsUntilScrollToBottomShowsUp
            ) {
                setShowScrollToBottom(true);
            }
        }

        if (showScrollToBottom) {
            if (
                event.currentTarget.scrollHeight -
                    (event.currentTarget.scrollTop + event.currentTarget.offsetHeight) <
                pixelsUntilScrollToBottomShowsUp
            ) {
                setShowScrollToBottom(false);
                setShowNewMessagesIndicator(false);
            }
        }
    };

    const navigateToLatestUnresolved = () => {
        if (!internalNotesWithOpenMention || internalNotesWithOpenMention.length === 0)
            return;

        const unresolvedNotes = internalNotesWithOpenMention.map((note) => note.id);
        const messageId = findChatMessageIdFromPath();
        const nextIndexToNavigate = messageId
            ? (unresolvedNotes.indexOf(messageId) + 1) % unresolvedNotes.length
            : 0;
        const nextMention = internalNotesWithOpenMention[nextIndexToNavigate];

        if (nextMention.id && nextMention.member) {
            const isMemberPath = findMemberIdFromPath();
            // Emitter call is needed so that scrolling into chat message works also within same url route
            EventEmitter.emit({
                name: "scrollChatIntoMessage",
                params: { messageId: nextMention.id },
            });
            history.push(
                routes.chatMessage(
                    nextMention.member,
                    nextMention.id,
                    isMemberPath ? undefined : nextMention.request || undefined,
                ),
            );
        }
    };

    const addFilesToAttachments = (files: FileList) => {
        EventEmitter.emit({
            name: "addFilesToChatAttachments",
            params: { files },
        });
    };

    return (
        <ChatSelectionProvider>
            <HandleDragAndDrop
                label="Drag and drop to add attachments..."
                className="chat"
                onDropFiles={addFilesToAttachments}
            >
                {internalNotesWithOpenMention &&
                    internalNotesWithOpenMention.length !== 0 && (
                        <div
                            className="chat-messages__scroll-to-task"
                            onClick={navigateToLatestUnresolved}
                        >
                            Unresolved tasks ({internalNotesWithOpenMention.length})
                        </div>
                    )}

                <div
                    className={chatMessagesClassName}
                    ref={chatMessagesRef}
                    onScroll={onScroll}
                >
                    {conversation && chatPaginatedState && (
                        <PagedChatMessages
                            tick={chatUpdateTick}
                            agents={agents}
                            conversation={conversation}
                            currentAgent={currentAgent}
                            member={member}
                            chatPagedState={chatPaginatedState}
                            request={request}
                        />
                    )}
                    {member.tenant === "mastercard" && (
                        <RequestStatusChatBadge
                            conversation={conversation.id}
                            request={request?.id}
                        />
                    )}
                </div>

                <div className="chat__reply-container">
                    <ChatSelectionActionBar
                        member={member}
                        currentAgent={currentAgent}
                        loadedMessages={loadedMessages}
                    />
                    <ChatReply
                        conversation={conversation}
                        request={request}
                        currentAgent={currentAgent}
                        member={member}
                    />
                    {showScrollToBottom && (
                        <Flex.Row
                            className={classNames("chat-messages__scroll-to-bottom", {
                                "chat-messages__scroll-to-bottom--new-messages":
                                    showNewMessagesIndicator,
                            })}
                            onClick={() => {
                                scrollRefToBottom(chatMessagesRef);
                                setShowNewMessagesIndicator(false);
                            }}
                            gap="medium"
                        >
                            <Icon.ArrowDownward.Small.N10 />
                            {showNewMessagesIndicator && (
                                <Label.Small.SemiBold.N10>
                                    New unread messages
                                </Label.Small.SemiBold.N10>
                            )}
                        </Flex.Row>
                    )}
                </div>
            </HandleDragAndDrop>
        </ChatSelectionProvider>
    );
}

export default Chat;
